import type { Dispatch } from '@atlaskit/editor-common/event-dispatcher';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { editorToolbarPluginKey } from '@atlaskit/editor-plugin-toolbar/plugin-key';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { RemixModalMetadata } from '../types';

export type MalleableUiPluginState = {
	/** localIds of MAUI embeds that were just replaced by an AI edit and are loading. */
	aiEditReplacementLocalIds: Set<string>;
	/**
	 * Variant-picker updates handled FE-side by `useUpdateRemix`/`useOnComplete`.
	 * The rovo-bridge update processors check this set to skip the BE-pushed
	 * duplicate insert. Key is `historyEntry.fieldId` (image) or the MAUI app
	 * id from `historyEntry.url` (MAUI); the two id spaces don't collide.
	 */
	inFlightVariantUpdates: Set<string>;
	/** Projection of the global Remix machine activity, written by EditorRemixEffectHost. */
	isAnyRemixing: boolean;
	isRemixModalOpen: boolean;
	/**
	 * contentId of the in-flight diagram remix's whiteboard, letting the ready-signal UI target that
	 * board node (not the thinking-widget anchor). Set at streaming-start; cleared on a new remix.
	 */
	remixDiagramContentId?: string;
	/**
	 * Media (file) id of the image the in-flight remix just inserted. Images are uploaded and
	 * inserted *after* the remix reports ready, so the thinking-widget anchor is stale by then —
	 * the ready-signal UI targets this media node instead. Cleared on a new remix.
	 */
	remixImageMediaId?: string;
	remixModalMetadata?: RemixModalMetadata;
	remixSource?: string;
	/** URL for the result inserted by the latest edit/change-type flow. */
	remixUpdateScrollTargetUrl?: string;
	showLocationPicker: boolean;
	/**
	 * Whether the "Output adjusted" flag is shown (backend produced a different
	 * type than selected). On durable plugin state so it survives the remix flow
	 * tearing down on completion.
	 */
	showSubtypeAdjustedFlag: boolean;
};

export type MalleableUiPluginMeta = {
	/** Add a localId to the AI edit replacement set. */
	addAiEditReplacementLocalId?: string;
	/** Add an id to the in-flight variant updates set (toolbar variant click). */
	addInFlightVariantUpdateId?: string;
	isAnyRemixing?: boolean;
	isRemixModalOpen?: boolean;
	/**
	 * The diagram remix's whiteboard contentId, or `undefined` to clear it. Presence is keyed on
	 * `'remixDiagramContentId' in meta`, so including `undefined` clears and omitting it keeps.
	 */
	remixDiagramContentId?: string;
	/**
	 * The media id of the image inserted by the remix, or `undefined` to clear it. Presence is keyed
	 * on `'remixImageMediaId' in meta`, so including `undefined` clears and omitting it keeps.
	 */
	remixImageMediaId?: string;
	remixModalMetadata?: RemixModalMetadata;
	remixSource?: string;
	/** URL for the result inserted by the latest edit/change-type flow, or `undefined` to clear it. */
	remixUpdateScrollTargetUrl?: string;
	/** Remove a localId from the AI edit replacement set (on embed load). */
	removeAiEditReplacementLocalId?: string;
	/** Remove an id from the in-flight variant updates set (useOnComplete done). */
	removeInFlightVariantUpdateId?: string;
	showLocationPicker?: boolean;
	/** Show/hide the "Output adjusted" subtype-mismatch flag. */
	showSubtypeAdjustedFlag?: boolean;
};

export const malleableUiPluginKey: PluginKey<MalleableUiPluginState> =
	new PluginKey<MalleableUiPluginState>('malleableUiPlugin');

/**
 * Transaction meta set by the Rovo bridge on the transaction that inserts or replaces an inline
 * Rovo edit's result. The remix thinking plugin treats it as the end of target tracking for the
 * active inline attempt, so the editor's own insertion is never mistaken for a user edit of the
 * target, whether or not the bridge managed to claim the attempt.
 */
export const INLINE_ROVO_EDIT_INSERTION_META = 'inlineRovoEditInsertion';

export function createToolbarSuppressionPlugin(): SafePlugin {
	return new SafePlugin({
		filterTransaction(tr, state) {
			// Block shouldShowToolbar:true transactions when the remix modal is open.
			// The mouseup listener on view.root fires even for clicks inside the remix
			// modal portal, which would re-show the toolbar. Filter those out.
			const toolbarMeta = tr.getMeta(editorToolbarPluginKey);
			if (toolbarMeta?.shouldShowToolbar === true) {
				const malleableState = malleableUiPluginKey.getState(state);
				if (malleableState?.isRemixModalOpen) {
					return false;
				}
			}
			return true;
		},
	});
}

export type CreateMalleableUiPmPluginOptions = {
	/**
	 * Seeds `isAnyRemixing` from the shared Remix registry. The editor core recreates its
	 * EditorState on schema/preset changes, which re-runs `init()` and would otherwise reset a
	 * projection that `EditorRemixEffectHost` has already written (CCCEMAU-3764). Only the editor
	 * treatment passes this; control keeps the legacy `false`.
	 */
	getInitialIsAnyRemixing?: () => boolean;
};

export function createMalleableUiPmPlugin(
	dispatch: Dispatch,
	options?: CreateMalleableUiPmPluginOptions,
): SafePlugin {
	return new SafePlugin({
		key: malleableUiPluginKey,
		state: {
			init(): MalleableUiPluginState {
				return {
					isRemixModalOpen: false,
					remixModalMetadata: undefined,
					showLocationPicker: false,
					aiEditReplacementLocalIds: new Set(),
					inFlightVariantUpdates: new Set(),
					isAnyRemixing: options?.getInitialIsAnyRemixing?.() ?? false,
					remixUpdateScrollTargetUrl: undefined,
					showSubtypeAdjustedFlag: false,
				};
			},
			apply(tr, pluginState): MalleableUiPluginState {
				const meta: MalleableUiPluginMeta | undefined = tr.getMeta(malleableUiPluginKey);

				if (!meta) {
					return pluginState;
				}

				let changed = false;
				let nextIds = pluginState.aiEditReplacementLocalIds;

				if (meta.addAiEditReplacementLocalId && !nextIds.has(meta.addAiEditReplacementLocalId)) {
					nextIds = new Set(nextIds);
					nextIds.add(meta.addAiEditReplacementLocalId);
					changed = true;
				}

				if (
					meta.removeAiEditReplacementLocalId &&
					nextIds.has(meta.removeAiEditReplacementLocalId)
				) {
					nextIds = new Set(nextIds);
					nextIds.delete(meta.removeAiEditReplacementLocalId);
					changed = true;
				}

				let nextInFlightVariantUpdates = pluginState.inFlightVariantUpdates;

				if (
					meta.addInFlightVariantUpdateId &&
					!nextInFlightVariantUpdates.has(meta.addInFlightVariantUpdateId)
				) {
					nextInFlightVariantUpdates = new Set(nextInFlightVariantUpdates);
					nextInFlightVariantUpdates.add(meta.addInFlightVariantUpdateId);
					changed = true;
				}

				if (
					meta.removeInFlightVariantUpdateId &&
					nextInFlightVariantUpdates.has(meta.removeInFlightVariantUpdateId)
				) {
					nextInFlightVariantUpdates = new Set(nextInFlightVariantUpdates);
					nextInFlightVariantUpdates.delete(meta.removeInFlightVariantUpdateId);
					changed = true;
				}

				if (
					meta.isRemixModalOpen !== undefined ||
					(meta.isAnyRemixing !== undefined && meta.isAnyRemixing !== pluginState.isAnyRemixing) ||
					changed ||
					meta.showSubtypeAdjustedFlag !== undefined ||
					'remixDiagramContentId' in meta ||
					'remixUpdateScrollTargetUrl' in meta ||
					'remixImageMediaId' in meta ||
					(meta.showLocationPicker !== undefined &&
						fg('platform_editor_ai_remix_in_editor_loading'))
				) {
					const result: MalleableUiPluginState = {
						isRemixModalOpen: meta.isRemixModalOpen ?? pluginState.isRemixModalOpen,
						remixModalMetadata:
							'remixModalMetadata' in meta
								? meta.remixModalMetadata
								: pluginState.remixModalMetadata,
						remixDiagramContentId:
							'remixDiagramContentId' in meta
								? meta.remixDiagramContentId
								: pluginState.remixDiagramContentId,
						remixImageMediaId:
							'remixImageMediaId' in meta ? meta.remixImageMediaId : pluginState.remixImageMediaId,
						remixSource: 'remixSource' in meta ? meta.remixSource : pluginState.remixSource,
						remixUpdateScrollTargetUrl:
							'remixUpdateScrollTargetUrl' in meta
								? meta.remixUpdateScrollTargetUrl
								: pluginState.remixUpdateScrollTargetUrl,
						showLocationPicker:
							meta.showLocationPicker !== undefined &&
							fg('platform_editor_ai_remix_in_editor_loading')
								? meta.showLocationPicker
								: pluginState.showLocationPicker,
						aiEditReplacementLocalIds: nextIds,
						inFlightVariantUpdates: nextInFlightVariantUpdates,
						isAnyRemixing: meta.isAnyRemixing ?? pluginState.isAnyRemixing,
						showSubtypeAdjustedFlag:
							meta.showSubtypeAdjustedFlag ?? pluginState.showSubtypeAdjustedFlag,
					};
					dispatch(malleableUiPluginKey, result);
					return result;
				}

				return pluginState;
			},
		},
	});
}
